import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import BlockData from '../models/BlockData';
import api from '../services/api';
import { PaginationMeta, PaginationMetaLink } from '../components/Modals/Paginator';
import { sortDisplayMapping, sortMapping } from '../utils/my';
import { PageStatusEnum } from '../enums/PageStatusEnum';
import config from '../config';

interface BlockDataSettings {
  sequence_number?: number,
  is_subpage?: boolean,
  subpage_path?: string,
  parent_url?: string,
  parent_status?: number,
  parent_location: {
    page: number,
    index: number,
  },
  is_parent_published?: boolean,
}

export interface BlockDataPerPage {
  id: string,
  status: number,
  params?: BlockData[],
  last_error?: string;
  page_setting?: PageSetting,
  subpages?: SubPage[],
  settings?: BlockDataSettings,
  created_at: string,
  updated_at: string,
}

export interface SubPage {
  id?: string,
  url_path: string,
  subpage_id: string,
  subpage_url: string,
  subpage_title: string,
  is_subpage_published: boolean,
  deleted?: boolean,
  custom_url?: string,
}

export interface SubPageOption {
  id: string,
  is_published: boolean,
  title: string,
  url_path: string,
}

export interface ParentPageProperties {
  url: string,
  path: string,
  is_published: boolean,
}

export interface Sitemap {
  exists: boolean,
  is_deleting?: boolean,
}
export interface Robots {
  exists?: boolean,
  is_deleting?: boolean,
}
export interface Llms {
  exists?: boolean,
  is_deleting?: boolean,
}

export interface PageSetting {
  title?: string
  description?: string
  keywords?: string
  favicon_url?: string
  favicon_file?: Blob
  url?: string
  custom_url?: string
  is_published?: boolean,
  sub_pages?: any[],
  smart_phone_width?: number
  desk_top_width?: number
  header_script?: string
  body_script?: string
  animation?: string
  google_analytics_script?: string
  is_robots_noindex?: boolean
  google_search_console_url?: string
  background_color?: string
  main_color?: string
  text_color?: string
  text_font?: string
  published_at?: string
  sitemap?: Sitemap
  uploaded_sitemap?: File
  parent_page_properties?: ParentPageProperties
  password?: string
  auth_page_description?: string
  should_deactivate_password_protection?: boolean
  robots?: Robots
  llms?: Llms
  uploaded_robots?: File
  uploaded_llms?: File
  www_status?: string
}

interface PageContextProps {
  pageIds?: string[]
  pageDatas?: BlockDataPerPage[]
  pageDatasSnapshot?: BlockDataPerPage[]
  Create?: (params: string, cb?: (id: string) => void) => void
  Update?: (id: string, params: string, cb?: () => void) => void
  Duplicate?: (id: string, cb?: () => void) => void
  UpdateSetting?: (id: string, body: PageSetting, cb?: () => void, ccb?: (error: string) => void) => void
  UpdateSiteProperties?: (id: string, body: PageSetting, cb?: () => void, ccb?: (error: string) => void) => void
  GetQrCode?: (id: string, body: PageSetting, cb?: (result: string, hasError?: boolean) => void) => void
  List?: (page?: number, sort?: string, display?: string) => void
  Get?: (id: string) => void
  Delete?: (id: string, sort: string, display: string) => void
  listLoading?: boolean
  pageDataLoading?: boolean
  updatePageLoading?: boolean
  total: number
  totalPublishedPages?: number
  paginationPageInfo?: PaginationMeta
  links?: PaginationMetaLink[]
  getPageList?: ({
    page,
    sort,
    display,
  }: { page?: number, sort?: string, display?: string, is_user_sites?: boolean }) => void
  getSubpageOptions?: (currentPageId: string, selectedSubpageId?: string) => Promise<any>
  getPageSubpages?: () => SubPage[]
  contextErrors?: any
  clearErrors?: () => void,
  checkForStatus?: (pageId: string) => void,
  clearApiInterval?: (pageId: string) => void,

}

export const PagesContext = createContext<PageContextProps>({
  total: 0,
  contextErrors: {},
});

interface PagesContextProviderProps {
  children: ReactNode
}

const PagesContextProvider: React.FC<PagesContextProviderProps> = (props: PagesContextProviderProps) => {
  const { children } = props;
  const [pageDatas, setPageDatas] = useState<BlockDataPerPage[]>([]);
  const [pageDatasSnapshot, setPageDatasSnapshot] = useState<BlockDataPerPage[]>([]);
  const [pageIds, setPageIds] = useState<string[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [pageDataLoading, setPageDataLoading] = useState(false);
  const [updatePageLoading, setUpdatePageLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPublishedPages, setTotalPublishedPages] = useState(0);
  const [paginationPageInfo, setPaginationPageInfo] = useState<PaginationMeta>({
    current_page: 1,
    from: 1,
    last_page: 1,
    links: [],
    path: '',
    per_page: 1,
    to: 1,
    total: 1,
  });
  const [links, setLinks] = useState(null);
  const [contextErrors, setErrors] = useState([]);

  useEffect(() => {
    setPageDatasSnapshot(pageDatas);
  }, [pageDatas]);

  const Create = useCallback(async (params: string, cb?: (id: string) => void) => {
    try {
      const res = await api.post('/v1/pages/create', { params });
      setPageIds((ids) => [...ids.filter((id) => id !== res.data), res.data]);
      if (cb != null) {
        cb(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const Update = useCallback((id: string, params: string, cb?: () => void) => {
    setUpdatePageLoading(true);
    api.patch(`/v1/pages/${id}/update`, { params }).then(() => {
      setUpdatePageLoading(false);
      if (cb != null) {
        cb();
      }
    });
  }, []);

  const UpdateSetting = useCallback((id: string, {
    title,
    description,
    keywords,
    favicon_url,
    favicon_file,
    smart_phone_width,
    desk_top_width,
    header_script,
    body_script,
    google_analytics_script,
    is_robots_noindex,
    url,
    animation,
    google_search_console_url,
    background_color,
    main_color,
    text_color,
    text_font,
    password,
    auth_page_description,
    should_deactivate_password_protection,
    robots,
    llms,
  }: PageSetting, cb?: () => void, ccb?: (error: any) => void) => {
    setErrors(null);
    setUpdatePageLoading(true);

    const formData = new FormData();
    if (title) formData.append('title', title);
    if (description != null) formData.append('description', description);
    if (keywords != null) formData.append('keywords', keywords);
    if (favicon_url) formData.append('favicon_url', favicon_url);
    if (favicon_file) formData.append('favicon_file', favicon_file as Blob);
    if (smart_phone_width) formData.append('smart_phone_width', String(smart_phone_width));
    if (desk_top_width) formData.append('desk_top_width', String(desk_top_width));
    if (header_script != null) formData.append('header_script', header_script);
    if (body_script != null) formData.append('body_script', body_script);
    if (google_analytics_script != null) formData.append('google_analytics_script', google_analytics_script);
    if (is_robots_noindex != null) formData.append('is_robots_noindex', String(is_robots_noindex));
    if (url != null) formData.append('url', String(url));
    if (animation != null) formData.append('animation', animation);
    if (google_search_console_url != null) formData.append('google_search_console_url', google_search_console_url);
    if (background_color != null) formData.append('background_color', background_color);
    if (main_color != null) formData.append('main_color', main_color);
    if (text_color != null) formData.append('text_color', text_color);
    if (text_font != null) formData.append('text_font', text_font);
    if (password != null) formData.append('password', password);
    if (auth_page_description != null) formData.append('auth_page_description', auth_page_description);
    if (should_deactivate_password_protection != null) formData.append('should_deactivate_password_protection', String(should_deactivate_password_protection));

    const pageSetting: PageSetting = {};
    if (title) pageSetting.title = title;
    if (description) pageSetting.description = description;
    if (keywords) pageSetting.keywords = keywords;
    if (favicon_url) pageSetting.favicon_url = favicon_url;
    if (favicon_file) pageSetting.favicon_file = favicon_file;
    if (smart_phone_width) pageSetting.smart_phone_width = smart_phone_width;
    if (desk_top_width) pageSetting.desk_top_width = desk_top_width;
    if (header_script) pageSetting.header_script = header_script;
    if (body_script) pageSetting.body_script = body_script;
    if (google_analytics_script) pageSetting.google_analytics_script = google_analytics_script;
    if (is_robots_noindex != null) pageSetting.is_robots_noindex = is_robots_noindex;
    if (url != null) pageSetting.url = url;
    if (animation != null) pageSetting.animation = animation;
    if (google_search_console_url != null) pageSetting.google_search_console_url = google_search_console_url;
    if (background_color != null) pageSetting.background_color = background_color;
    if (main_color != null) pageSetting.main_color = main_color;
    if (text_color != null) pageSetting.text_color = text_color;
    if (text_font != null) pageSetting.text_font = text_font;
    if (password != null) pageSetting.password = password;
    if (auth_page_description != null) pageSetting.auth_page_description = auth_page_description;
    if (should_deactivate_password_protection != null) pageSetting.should_deactivate_password_protection = should_deactivate_password_protection;

    api.post(`/v1/pages/${id}/setting/update`, formData, {
      headers: { 'content-type': 'multipart/form-data' },
    }).then(() => {
      if (cb) cb();

      document.documentElement.style.setProperty('--desktop-width', `${pageSetting.desk_top_width}px`);
      document.documentElement.style.setProperty('--smartphone-width', `${pageSetting.smart_phone_width}px`);

      setPageDatas((prevPageDatas) => prevPageDatas.map((page) => {
        if (page.id === id) {
          return {
            ...page,
            page_setting: {
              ...page.page_setting,
              ...pageSetting,
            },
          };
        }
        return page;
      }));
    }).catch((error) => {
      setErrors(error.response.data.errors);
      if (ccb) ccb(error);
    }).finally(() => {
      setUpdatePageLoading(false);
    });
  }, [pageDatas]);

  const UpdateSiteProperties = useCallback((id: string, {
    title,
    custom_url,
    sub_pages,
    uploaded_sitemap,
    sitemap,
    uploaded_robots,
    robots,
    uploaded_llms,
    llms,
    www_status,
  }: PageSetting, cb?: () => void, ccb?: (error: any) => void) => {
    setErrors(null);

    const formData = new FormData();
    if (title != null) formData.append('title', title);
    if (custom_url != null) formData.append('custom_url', custom_url);
    if (sub_pages != null) formData.append('sub_pages', JSON.stringify(sub_pages));
    if (uploaded_sitemap != null) formData.append('sitemap', uploaded_sitemap);
    if (sitemap != null) formData.append('sitemap_object', JSON.stringify(sitemap));
    if (uploaded_robots != null) formData.append('robots', uploaded_robots);
    if (robots != null) formData.append('robots_object', JSON.stringify(robots));
    if (uploaded_llms != null) formData.append('llms', uploaded_llms);
    if (llms != null) formData.append('llms_object', JSON.stringify(llms));
    if (www_status != null) formData.append('www_status', String(www_status));

    const pageSetting: PageSetting = {};
    if (title != null) pageSetting.title = title;
    if (custom_url != null) pageSetting.custom_url = custom_url;
    if (sub_pages != null) pageSetting.sub_pages = sub_pages;
    if (uploaded_sitemap != null) pageSetting.uploaded_sitemap = uploaded_sitemap;
    if (sitemap != null) pageSetting.sitemap = sitemap;
    if (uploaded_robots != null) pageSetting.uploaded_robots = uploaded_robots;
    if (robots != null) pageSetting.robots = robots;
    if (uploaded_llms != null) pageSetting.uploaded_llms = uploaded_llms;
    if (llms != null) pageSetting.llms = llms;
    if (www_status != null) pageSetting.www_status = www_status;

    api.post(`/v1/pages/${id}/site/properties/update`, formData, {
      headers: { 'content-type': 'multipart/form-data' },
    }).then(() => {
      if (cb) cb();

      setPageDatas((pageDatas) => pageDatas.map((page) => {
        if (page.id === id) {
          return { ...page, page_setting: { ...page.page_setting, ...pageSetting }, subpages: sub_pages };
        }
        return page;
      }));
    }).catch((error) => {
      setErrors(error.response.data.errors);
      if (ccb) ccb(error);
    });
  }, []);

  const List = useCallback(async (page?: number, sort = 'updated_at_newer', display = 'all') => {
    setListLoading(true);

    page = page || paginationPageInfo.current_page;

    const { display_option } = sortDisplayMapping[display] || sortDisplayMapping.all;
    const { field, order } = sortMapping[sort] || sortMapping.updated_at_newer;

    const result = await api.get(`/v1/pages?page=${page}&sort=${field}&order=${order}&display_option=${display_option}`);

    setListLoading(false);
    const { data } = result.data;

    setPaginationPageInfo(result.data.meta);
    setTotalPublishedPages(result.data.meta.total_published);
    setTotal(result.data.meta.total);
    setLinks(result.data.meta.links);

    if (!Array.isArray(data)) {
      return;
    }

    const allPagesBlockData: BlockDataPerPage[] = [];
    const pageIds: string[] = [];
    for (let i = 0; i < data.length; i++) {
      const obj: BlockDataPerPage = {
        id: data[i].id,
        status: data[i].status,
        last_error: data[i].last_error,
        created_at: data[i].created_at,
        updated_at: data[i].updated_at,
        page_setting: data[i].page_setting,
        subpages: data[i].subpages,
        settings: data[i].settings,
      };
      if (data[i].params != null && data[i].params.length > 0) {
        try {
          const dataPerPage = JSON.parse(data[i].params) as BlockData[];
          obj.params = dataPerPage;
        } catch (e) {
          console.error('failed at parse params');
        }
      }

      allPagesBlockData.push(obj);
      pageIds.push(data[i].id);
    }

    setPageDatas(allPagesBlockData);
    setPageIds(pageIds);
  }, [paginationPageInfo.current_page]);

  const requestPageList = useCallback(async ({
    page,
    sort,
    display,
  }: { page?: number, sort?: string, display?: string }) => {
    List(page, sort, display);
  }, [List]);

  const Get = useCallback(async (id: string) => {
    setPageDataLoading(true);
    const result = await api.get(`/v1/pages/${id}`);
    setPageDataLoading(false);
    const { data } = result;

    const blockDataPerPage: BlockDataPerPage = {
      id: data.id,
      status: data.status,
      last_error: data.last_error,
      created_at: data.created_at,
      updated_at: data.updated_at,
    };

    // params would be a huge data
    if (data.params != null && data.params.length > 0) {
      try {
        const params = JSON.parse(data.params) as BlockData[];
        blockDataPerPage.params = params;
      } catch (e) {
        console.error('failed at parse params');
      }
    }

    if (data.subpages != null && data.subpages.length > 0) {
      blockDataPerPage.subpages = data.subpages;
    }

    if (data.page_setting != null) {
      blockDataPerPage.page_setting = {
        title: data.page_setting.title,
        description: data.page_setting.description,
        keywords: data.page_setting.keywords,
        favicon_url: data.page_setting.favicon_url,
        url: data.page_setting.url,
        custom_url: data.page_setting.custom_url,
        smart_phone_width: data.page_setting.smart_phone_width,
        desk_top_width: data.page_setting.desk_top_width,
        header_script: data.page_setting.header_script,
        body_script: data.page_setting.body_script,
        animation: data.page_setting.animation,
        google_analytics_script: data.page_setting.google_analytics_script,
        is_robots_noindex: data.page_setting.is_robots_noindex,
        is_published: data.page_setting.is_published,
        google_search_console_url: data.page_setting.google_search_console_url,
        background_color: data.page_setting.background_color,
        main_color: data.page_setting.main_color,
        text_color: data.page_setting.text_color,
        text_font: data.page_setting.text_font,
        parent_page_properties: data.page_setting.parent_page_properties,
        sitemap: data.page_setting.sitemap,
        password: data.page_setting.password,
        auth_page_description: data.page_setting.auth_page_description,
      };
      document.documentElement.style.setProperty('--desktop-width', blockDataPerPage.page_setting.desk_top_width ? `${blockDataPerPage.page_setting.desk_top_width}px` : '100%');
      document.documentElement.style.setProperty('--smartphone-width', blockDataPerPage.page_setting.smart_phone_width ? `${blockDataPerPage.page_setting.smart_phone_width}px` : '100%');
    } else {
      document.documentElement.style.setProperty('--desktop-width', '100%');
      document.documentElement.style.setProperty('--smartphone-width', '100%');
    }

    setPageDatas((pageDatas) => {
      if (pageDatas.some((p) => p.id === blockDataPerPage.id)) {
        return pageDatas.map((data) => {
          if (data.id === blockDataPerPage.id) {
            return blockDataPerPage;
          }
          return data;
        });
      }

      return [...pageDatas, blockDataPerPage];
    });
  }, []);

  const Delete = useCallback(async (id: string, sort: string, display: string) => {
    setErrors(null);

    await api.delete(`/v1/pages/${id}/delete`)
      .then(() => {
        setPageIds((ids) => ids.filter((_id) => _id !== id));
        setPageDatas((pageDatas) => pageDatas.filter((p) => p.id !== id));
        // Refetch the list of pages to ensure the state is in sync with the server
        List(paginationPageInfo.current_page, sort, display);
      })
      .catch((error) => {
        setErrors(error.response.data);
      });
    // 削除が失敗になった場合
    // If the deletion is successful, optimistically update the state
  }, [List, paginationPageInfo.current_page]);

  const Duplicate = useCallback((id: string, cb?: () => void) => {
    setUpdatePageLoading(true);
    api.post(`/v1/pages/${id}/duplicate`).then(() => {
      if (cb != null) {
        cb();
      }
    }).finally(() => {
      setUpdatePageLoading(false);
    });
  }, []);

  const GetQrCode = useCallback((id: string, props: PageSetting, cb?: (data: string, hasError?: boolean) => void) => {
    console.log('calling QrDownload API!!!', [id, props, cb]);
    api.patch(`/v1/pages/${id}/setting/qrCode`, props)
      .then((result) => {
        if (cb != null) {
          cb(result.data);
        }
      })
      .catch((error) => {
        cb(error?.response?.data?.message, true);
        console.error('QrDownload error: ', error);
      });
  }, []);

  const getSubpageOptions = useCallback(async (currentPageId: string, selectedSubpageId?: string) => {
    const result = await api.post('/v1/pages/subpage-options', { currentPageId, selectedSubpageId });
    return result;
  }, []);

  const getPageSubpages = useCallback(() => {
    const subpages: SubPage[] = [];
    const pageId = window.location.pathname.split('/').pop();

    if (pageId == null) {
      return subpages;
    }

    const pageData = pageDatas.find((page) => page.id === pageId);

    if (pageData.page_setting.parent_page_properties && !pageData.page_setting.custom_url) {
      subpages.push({
        url_path: '',
        subpage_id: pageData.page_setting.parent_page_properties.url,
        subpage_url: '',
        subpage_title: 'Home',
        is_subpage_published: false,
        custom_url: pageData.page_setting.parent_page_properties.url,
      });
    }

    pageDatas.forEach((page) => {
      if (page.subpages != null && page.id === pageId) {
        page.subpages.forEach((subpage) => {
          if (subpage.deleted) {
            return;
          }

          subpages.push({
            ...subpage,
            custom_url: page.page_setting?.parent_page_properties?.url ?? page.page_setting.custom_url,
          });
        });
      }
    });
    return subpages;
  }, [pageDatas]);

  const clearErrors = useCallback(() => setErrors([]), []);

  // CALLS ---------------------------------------------------------------
  interface apiCallType {
    page_id: string,
    current_call: number,
    interval_id: NodeJS.Timer,
  }

  const maxApiCalls = config.api.max_calls;
  const apiCallIntervalTime = config.api.call_interval;
  const [currentRunningApiCalls, setCurrentRunningApiCalls] = useState<apiCallType[]>([]);

  const getPageStatus = useCallback(async (pageId: string) => {
    await api.get(`/v1/pages/${pageId}/status`).then((res) => {
      const { status } = res.data;
      const pageData = pageDatas.find((page) => page.id === pageId);

      if (pageData && pageData.status !== status) {
        if (status !== PageStatusEnum.PROCESSING) {
          requestPageList({ page: paginationPageInfo.current_page });
        }

        setPageDatas((pageDatas) => pageDatas.map((page) => {
          // Update status for the current page
          if (page.id === pageId) {
            return { ...page, status };
          }

          // Update parent status for children whose parent_url matches this page's custom_url
          if (page.settings?.parent_url === pageData?.page_setting?.custom_url) {
            return {
              ...page,
              settings: {
                ...page.settings,
                parent_status: status,
                is_parent_published: false,
              },
            };
          }

          return page;
        }));
      }
    });
  }, [pageDatas, paginationPageInfo.current_page, requestPageList]);

  const checkForStatus = useCallback((pageId: string) => {
    getPageStatus(pageId);

    // create interval to check page status
    const interval = setInterval(() => {
      getPageStatus(pageId);
      // update current call count
      setCurrentRunningApiCalls((prev) => {
        const newApiCalls = prev.map((call) => {
          if (call.page_id === pageId) {
            return { ...call, current_call: call.current_call + 1 };
          }
          return call;
        });

        return newApiCalls;
      });
    }, apiCallIntervalTime);

    // mount object
    const apiCallObject = {
      page_id: pageId,
      current_call: 0,
      interval_id: interval,
    };

    // add to current running api calls
    setCurrentRunningApiCalls((prev) => {
      const newApiCalls = [...prev, apiCallObject];
      return newApiCalls;
    });
  }, [apiCallIntervalTime, getPageStatus]);

  const clearApiInterval = useCallback((pageId: string) => {
    setCurrentRunningApiCalls((prev) => {
      const apiCallObject = prev.find((call) => call.page_id === pageId);

      if (apiCallObject) clearInterval(apiCallObject.interval_id);
      const newApiCalls = prev.filter((call) => call.page_id !== pageId);
      return newApiCalls;
    });
  }, []);

  useLayoutEffect(() => {
    if (currentRunningApiCalls.length > 0) {
      currentRunningApiCalls.forEach((call) => {
        // check if current call is greater than max api calls
        console.log(`current call ${call.current_call}/${maxApiCalls} for page ${call.page_id}`);

        if (call.current_call >= maxApiCalls) {
          clearApiInterval(call.page_id);
        }

        // check if page is not processing
        const pageData = pageDatas.find((page) => page.id === call.page_id);
        if (
          pageData
          && call.current_call > 2
          && pageData.status !== PageStatusEnum.PROCESSING
        ) {
          clearApiInterval(call.page_id);
        }
      });
    }
  }, [maxApiCalls, currentRunningApiCalls, clearApiInterval, pageDatas]);
  // END OF CALLS ---------------------------------------------------------------

  const values = useMemo<PageContextProps>(() => ({
    Create,
    List,
    Update,
    UpdateSetting,
    UpdateSiteProperties,
    Get,
    Delete,
    pageIds,
    pageDatas,
    pageDatasSnapshot,
    listLoading,
    pageDataLoading,
    updatePageLoading,
    total,
    totalPublishedPages,
    paginationPageInfo,
    links,
    getPageList: requestPageList,
    Duplicate,
    GetQrCode,
    getSubpageOptions,
    getPageSubpages,
    clearErrors,
    checkForStatus,
    clearApiInterval,
  }), [Create, List, Update, UpdateSetting, UpdateSiteProperties, Get, Delete, pageIds, pageDatas, pageDatasSnapshot, listLoading, pageDataLoading, updatePageLoading, total, totalPublishedPages, paginationPageInfo, links, requestPageList, Duplicate, GetQrCode, getSubpageOptions, getPageSubpages, clearErrors, checkForStatus, clearApiInterval]);

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <PagesContext.Provider value={{ ...values, contextErrors }}>
      {children}
    </PagesContext.Provider>
  );
};

export default PagesContextProvider;
